import { getDatabase, ref, update } from 'firebase/database';
import { BuildingInfo } from '../types/protocols';
import { requireAdmin } from './adminGuard';
import { recordActivity } from './activityApi';

/**
 * Type for Firebase updates that allows null to delete fields.
 * Firebase update() interprets null as "delete this field".
 */
export type BuildingInfoUpdate = {
    [K in keyof BuildingInfo]: BuildingInfo[K] | null;
};

/**
 * API for building info operations.
 * Handles updates to buildingsSummary/{buildingKey}/name and buildingInfo
 */
export const buildingInfoApi = {
    /**
     * Update the building's name and info fields in one write. Requires admin.
     * Pass null in `updates` to delete a field.
     * @param buildingKey - The building's unique key
     * @param name - The building name (trimmed, non-empty)
     * @param updates - Partial BuildingInfo with null for deletions
     */
    async updateBuildingInfo(
        buildingKey: string,
        name: string,
        updates: Partial<BuildingInfoUpdate>
    ): Promise<void> {
        if (!buildingKey) {
            throw new Error('buildingKey krävs');
        }
        const trimmedName = name.trim();
        if (!trimmedName) {
            throw new Error('Namn krävs');
        }

        const db = getDatabase();
        await requireAdmin(db, 'ändra byggnadens uppgifter');
        const changes: Record<string, unknown> = { name: trimmedName };
        for (const [field, value] of Object.entries(updates)) {
            changes[`buildingInfo/${field}`] = value;
        }
        await update(ref(db, `buildingsSummary/${buildingKey}`), changes);
        recordActivity('building.updated', { buildingKey, targetKey: buildingKey, label: trimmedName });
    },
};
